// frontend/src/pages/ManageOrders.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import RatingForm from '../components/RatingForm';
import { messaging, onMessage } from '../firebase';
import './Home.css'; // Global styles
import './ManageOrders.css';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const API_URL = process.env.REACT_APP_API_URL;
const WS_PROTOCOL = API_URL.startsWith('https') ? 'wss://' : 'ws://';
const WS_HOST = API_URL.replace(/^https?:\/\//, '');
const WS_URL = `${WS_PROTOCOL}${WS_HOST}`;

const fetchOrders = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/provider_orders/`, { withCredentials: true });
    return response.data;
  } catch (err) {
    console.error('Fetch provider orders failed:', err.response?.data || err.message);
    throw err;
  }
};

const fetchClientOrders = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/client_orders/`, { withCredentials: true });
    return response.data;
  } catch (err) {
    console.error('Fetch client orders failed:', err.response?.data || err.message);
    throw err;
  }
};

const fetchServiceMinimumCharge = async (requestId) => {
  try {
    const response = await axios.get(`${API_URL}/api/request/${requestId}/minimum_charge/`, { withCredentials: true });
    return response.data.minimum_charge;
  } catch (err) {
    console.error('Fetch minimum charge failed:', err.response?.data || err.message);
    throw err;
  }
};

const fetchOrderProgress = async (requestId) => {
  const response = await axios.get(`${API_URL}/api/order_progress/${requestId}/`, { withCredentials: true });
  return response.data;
};

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'checkout_initiated', label: 'Checkout Initiated' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'voided', label: 'Voided' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending_payment', label: 'Pending Payment' },
];

// OrderSection component
const OrderSection = React.memo(
  ({
    title,
    orders,
    filters,
    setFilters,
    currentPage,
    setCurrentPage,
    isCompleted,
    isProviderSection,
    sectionKey,
    providerOrders,
    handleAccept,
    handleReject,
    handleInitiateCheckout,
    handleProceedToCheckout,
    handleConfirmCash,
    handleInitiateStkPush,
    getStatusColor,
    canInitiateCheckout,
    showCheckoutPanel,
    setShowCheckoutPanel,
    showPaymentPanel,
    setShowPaymentPanel,
    checkoutAmount,
    setCheckoutAmount,
    providerPhone,
    setProviderPhone,
    paymentMethod,
    setPaymentMethod,
    clientPhone,
    setClientPhone,
    queryClient,
    refetchClientOrders,
    refetchProviderOrders,
    user,
    orderRefs,
    acceptMutation,
    rejectMutation,
    initiateCheckoutMutation,
    proceedToCheckoutMutation,
    confirmCashMutation,
    initiateStkPushMutation,
  }) => {
    const sectionRef = useRef(null);
    const ordersPerPage = 10;

    const filterOrders = useCallback((orders, filters, section) => {
      if (!orders || !Array.isArray(orders)) return [];

      return orders.filter((req) => {
        let status = req.status;
        if (section === 'completed') {
          status = req.order?.status === 'completed' ? 'completed' : req.status;
        } else {
          status = req.order?.status || req.status;
        }

        const matchesStatus = !filters.statuses.length || filters.statuses.some((s) => s.value === status);
        const matchesSearch = !filters.search || (req.description && req.description.toLowerCase().includes(filters.search.toLowerCase()));
        return matchesStatus && matchesSearch;
      });
    }, []);

    const paginateOrders = useCallback((orders, page) => {
      if (!orders || !Array.isArray(orders)) return [];
      const start = (page - 1) * ordersPerPage;
      const end = start + ordersPerPage;
      return orders.slice(start, end);
    }, []);

    const [minimumCharges, setMinimumCharges] = useState({});

    useEffect(() => {
      if (showCheckoutPanel) {
        fetchServiceMinimumCharge(showCheckoutPanel)
          .then((charge) => {
            setMinimumCharges((prev) => ({ ...prev, [showCheckoutPanel]: charge }));
          })
          .catch(() => {
            setMinimumCharges((prev) => ({ ...prev, [showCheckoutPanel]: null }));
          });
      }
    }, [showCheckoutPanel]);

    console.log(`[${title}] Orders:`, orders);
    console.log(`[${title}] Filters:`, filters);
    console.log(`[${title}] isCompleted:`, isCompleted);

    const filteredOrders = useMemo(
      () => filterOrders(orders, filters, isCompleted ? 'completed' : 'pending'),
      [orders, filters, isCompleted, filterOrders]
    );

    console.log(`[${title}] Filtered Orders:`, filteredOrders);

    const paginatedOrders = useMemo(
      () => paginateOrders(filteredOrders, currentPage),
      [filteredOrders, currentPage, paginateOrders]
    );

    const aiohttp = useMemo(
      () =>
        debounce((value) => {
          setFilters((prev) => ({ ...prev, search: value }));
          setCurrentPage(1);
        }, 300),
      [setFilters, setCurrentPage]
    );

    const handleSearchChange = useCallback(
      (e) => {
        const value = e.target.value;
        setFilters((prev) => ({ ...prev, searchInput: { ...prev.searchInput, [sectionKey]: value } }));
        aiohttp(value);
      },
      [sectionKey, aiohttp, setFilters]
    );

    const handleStatusChange = useCallback(
      (selected) => {
        setFilters((prev) => ({ ...prev, statuses: selected || [] }));
        setCurrentPage(1);
      },
      [setFilters, setCurrentPage]
    );

    const confirmProgressMutation = useMutation({
      mutationFn: async ({ requestId, notes }) => {
        const response = await axios.post(
          `${API_URL}/api/confirm_progress/${requestId}/`,
          { notes },
          { withCredentials: true }
        );
        return response.data;
      },
      onSuccess: () => {
        toast.success('Progress confirmed successfully.');
        queryClient.invalidateQueries(['orderProgress']);
        queryClient.invalidateQueries(['providerOrders']);
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to confirm progress.');
      },
    });

    const handleConfirmProgress = useCallback(
      (requestId) => {
        confirmProgressMutation.mutate({ requestId, notes: '' });
      },
      [confirmProgressMutation]
    );

    useEffect(() => {
      if (sectionRef.current && (showCheckoutPanel || showPaymentPanel)) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, [currentPage, filteredOrders.length, showCheckoutPanel, showPaymentPanel]);

    const Pagination = useCallback(
      ({ currentPage, totalItems, onPageChange }) => {
        const totalPages = Math.ceil(totalItems / ordersPerPage);
        const maxPagesToShow = 5;
        const pages = [];

        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage + 1 < maxPagesToShow) {
          startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }

        return (
          <div className="pagination flex gap-2 justify-center mt-4">
            <button
              className="btn btn-blue px-3 py-1 transition-transform duration-200 hover:scale-105 disabled:opacity-50"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {startPage > 1 && (
              <>
                <button className="btn btn-blue px-3 py-1" onClick={() => onPageChange(1)}>
                  1
                </button>
                {startPage > 2 && <span className="text-white">...</span>}
              </>
            )}
            {pages.map((page) => (
              <button
                key={page}
                className={`btn px-3 py-1 transition-transform duration-200 hover:scale-105 ${
                  page === currentPage ? 'btn-purple' : 'btn-blue'
                }`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            ))}
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <span className="text-white">...</span>}
                <button className="btn btn-blue px-3 py-1" onClick={() => onPageChange(totalPages)}>
                  {totalPages}
                </button>
              </>
            )}
            <button
              className="btn btn-blue px-3 py-1 transition-transform duration-200 hover:scale-105 disabled:opacity-50"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        );
      },
      []
    );

    return (
      <div className="order-card mb-8" ref={sectionRef}>
        <h2 className="text-2xl font-semibold mb-4 text-blue-300">{title}</h2>
        <div className="filter-container mb-4 flex flex-col sm:flex-row gap-2">
          <Select
            isMulti
            options={statusOptions}
            value={filters.statuses}
            onChange={handleStatusChange}
            placeholder="Filter status..."
            className="flex-1 text-sm"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <input
            type="text"
            value={filters.searchInput?.[sectionKey] || ''}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder="Search description..."
            className="input flex-1 text-sm"
            autoComplete="off"
          />
          <button
            className="btn btn-red text-sm px-3 py-1 transition-transform duration-200 hover:scale-105"
            onClick={() => {
              setFilters((prev) => ({
                ...prev,
                statuses: [],
                search: '',
                searchInput: { ...prev.searchInput, [sectionKey]: '' },
              }));
              setCurrentPage(1);
            }}
          >
            Reset
          </button>
        </div>
        {filteredOrders.length ? (
          <>
            {isProviderSection && providerOrders?.debt_message && (
              <p className="text-red-500 mb-4">{providerOrders.debt_message}</p>
            )}
            {isProviderSection && providerOrders?.is_restricted && (
              <p className="text-red-500 mb-4 font-semibold">
                Your account is restricted due to excessive warnings ({providerOrders.warning_count}/3). Contact support to resolve.
              </p>
            )}
            {isProviderSection && (
              <p className="mb-4">
                <strong>Trust Score:</strong> {providerOrders?.trust_score?.toFixed(1) || '0.0'} (Tier{' '}
                {providerOrders?.trust_tier || 'E'})
              </p>
            )}
            {paginatedOrders.map((req) => {
              const b2cTx = req.order?.mpesa_transactions?.find((tx) => tx.transaction_type === 'B2C');
              return (
                <div key={req.id} className="order-item relative" ref={(el) => (orderRefs.current[req.id] = el)}>
                  <p>
                    <strong>{isProviderSection ? 'Client' : 'Provider'}:</strong>{' '}
                    {isProviderSection
                      ? req.user_username || 'Unknown Client'
                      : req.provider_business_name || 'Unknown Provider'}
                  </p>
                  <p>
                    <strong>Description:</strong> {req.description || 'No description provided'}
                  </p>
                  <p>
                    <strong>Scheduled:</strong>{' '}
                    {req.scheduled_at ? new Date(req.scheduled_at).toLocaleString() : 'Not set'}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={getStatusColor(req.order?.status || req.status)}>
                      {(req.order?.status || req.status).replace('_', ' ')}
                    </span>
                  </p>
                  {req.order && (
                    <>
                      <p>
                        <strong>Amount:</strong> {req.order.amount ? `${req.order.amount} KES` : 'N/A'}
                      </p>
                      <p>
                        <strong>Payment Method:</strong> {req.order.payment_method || 'Pending'}
                      </p>
                      {isProviderSection && b2cTx && (
                        <>
                          <p>
                            <strong>Disbursement Status:</strong>{' '}
                            <span className={getStatusColor(req.order.disbursement_status || 'N/A')}>
                              {req.order.disbursement_status || 'N/A'}
                            </span>
                          </p>
                          <p>
                            <strong>Net Payout:</strong> {b2cTx.amount ? `${b2cTx.amount} KES` : 'N/A'}
                          </p>
                          <p>
                            <strong>B2C Fee Share:</strong> {b2cTx.b2c_fee ? `${b2cTx.b2c_fee} KES` : '0 KES'}
                          </p>
                        </>
                      )}
                    </>
                  )}
                  {req.order && req.order.rating && (
                    <p>
                      <strong>Rating:</strong> {req.order.rating.rating}/5{' '}
                      {req.order.rating.review && `(${req.order.rating.review})`} by{' '}
                      {req.order.rating.display_name}
                    </p>
                  )}
                  {req.status === 'active' && (
                    <>
                      <p className="text-yellow-500">Providers must confirm progress every 24 hours, or the order will be canceled.</p>
                      {isProviderSection && (
                        <p className="text-red-500">
                          Warning Count: {req.warning_count || 0}/3. Three warnings result in account restriction.
                        </p>
                      )}
                      <ProgressTimer requestId={req.id} />
                    </>
                  )}
                  {/* Confirm Progress Button (Active Only) */}
                  {isProviderSection && req.status === 'active' && req.user_id && (
                    <div className="mt-2 btn-container">
                      <button
                        type="button"
                        className="btn btn-green transition-transform duration-200 hover:scale-105 disabled:opacity-50"
                        onClick={(e) => {
                          e.preventDefault();
                          handleConfirmProgress(req.id);
                        }}
                        disabled={req.is_overdue || confirmProgressMutation.isLoading}
                      >
                        {confirmProgressMutation.isLoading ? 'Confirming...' : 'Confirm Progress'}
                      </button>
                    </div>
                  )}
                  {/* Chat with Client/Provider Button (Scheduled or Active Only) */}
                  {(req.status === 'scheduled' || req.status === 'active') && (
                    <>
                      {isProviderSection && req.user_id && (
                        <div className="mt-2 btn-container">
                          <Link
                            to={`/profile/?chat_with=${req.user_id}`}
                            className="btn btn-blue transition-transform duration-200 hover:scale-105"
                          >
                            Chat with Client
                          </Link>
                        </div>
                      )}
                      {!isProviderSection && req.provider_user_id && (
                        <div className="mt-2 btn-container">
                          <Link
                            to={`/profile/?chat_with=${req.provider_user_id}`}
                            className="btn btn-blue transition-transform duration-200 hover:scale-105"
                          >
                            Chat with Provider
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                  {isProviderSection && req.status === 'pending' && (
                    <div className="mt-2 btn-container">
                      <button
                        type="button"
                        className={`btn btn-green transition-transform duration-200 hover:scale-105 ${
                          providerOrders?.can_accept_orders && !req.is_restricted ? '' : 'bg-gray-600 cursor-not-allowed'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleAccept(req.id);
                        }}
                        disabled={!providerOrders?.can_accept_orders || req.is_restricted || acceptMutation.isLoading}
                      >
                        {acceptMutation.isLoading && acceptMutation.variables === req.id ? (
                          <>
                            <span className="spinner"></span> Accepting...
                          </>
                        ) : (
                          'Accept'
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-red transition-transform duration-200 hover:scale-105"
                        onClick={(e) => {
                          e.preventDefault();
                          handleReject(req.id);
                        }}
                        disabled={rejectMutation.isLoading && rejectMutation.variables === req.id}
                      >
                        {rejectMutation.isLoading && rejectMutation.variables === req.id ? (
                          <>
                            <span className="spinner"></span> Rejecting...
                          </>
                        ) : (
                          'Reject'
                        )}
                      </button>
                    </div>
                  )}
                  {isProviderSection && canInitiateCheckout(req) && req.status === 'active' && !req.order && (
                    <div className="mt-2 btn-container">
                      <button
                        type="button"
                        className="btn btn-blue transition-transform duration-200 hover:scale-105"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowCheckoutPanel(req.id);
                          setCheckoutAmount('');
                          setProviderPhone('');
                        }}
                        disabled={initiateCheckoutMutation.isLoading}
                      >
                        {initiateCheckoutMutation.isLoading && initiateCheckoutMutation.variables?.requestId === req.id ? (
                          <>
                            <span className="spinner"></span> Initiating...
                          </>
                        ) : (
                          'Initiate Checkout'
                        )}
                      </button>
                    </div>
                  )}
                  {showCheckoutPanel === req.id && (
                    <div className="checkout-panel open">
                      <h2 className="text-xl font-semibold mb-2">Initiate Checkout</h2>
                      {minimumCharges[req.id] && (
                        <p className="mb-2 text-sm text-blue-300">
                          Minimum charge for this service: {minimumCharges[req.id]} KES
                        </p>
                      )}
                      <form noValidate onSubmit={(e) => e.preventDefault()}>
                        <input
                          type="number"
                          inputMode="decimal"
                          value={checkoutAmount}
                          onChange={(e) => {
                            e.stopPropagation();
                            setCheckoutAmount(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') handleInitiateCheckout(req.id);
                          }}
                          onFocus={(e) => e.target.select()}
                          placeholder="Amount charged (KES)"
                          className="input mb-2 text-sm"
                          min="0.01"
                          step="0.01"
                          autoComplete="off"
                        />
                        <input
                          type="text"
                          value={providerPhone}
                          onChange={(e) => {
                            e.stopPropagation();
                            setProviderPhone(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') handleInitiateCheckout(req.id);
                          }}
                          onFocus={(e) => e.target.select()}
                          placeholder="M-Pesa Number (07XXXXXXXX or 01XXXXXXXX)"
                          className="input mb-2 text-sm"
                          autoComplete="off"
                        />
                      </form>
                      <div className="btn-container mt-2">
                        <button
                          type="button"
                          className="btn btn-green flex-1 text-sm transition-transform duration-200 hover:scale-105"
                          onClick={(e) => {
                            e.preventDefault();
                            handleInitiateCheckout(req.id);
                          }}
                          disabled={!checkoutAmount || !providerPhone || initiateCheckoutMutation.isLoading}
                        >
                          {initiateCheckoutMutation.isLoading && initiateCheckoutMutation.variables?.requestId === req.id ? (
                            <>
                              <span className="spinner"></span> Submitting...
                            </>
                          ) : (
                            'Submit'
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-red flex-1 text-sm transition-transform duration-200 hover:scale-105"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowCheckoutPanel(null);
                            setCheckoutAmount('');
                            setProviderPhone('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {isProviderSection &&
                    req.order &&
                    req.order.payment_method === 'cash' &&
                    req.order.status === 'pending_payment' && (
                      <div className="mt-2 btn-container">
                        <button
                          type="button"
                          className="btn btn-yellow transition-transform duration-200 hover:scale-105"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowPaymentPanel(req.id);
                            setCheckoutAmount('');
                          }}
                          disabled={confirmCashMutation.isLoading}
                        >
                          {confirmCashMutation.isLoading && confirmCashMutation.variables?.orderId === req.order?.id ? (
                            <>
                              <span className="spinner"></span> Confirming...
                            </>
                          ) : (
                            'Confirm Cash Received'
                          )}
                        </button>
                      </div>
                    )}
                  {!isProviderSection && req.order && req.order.status === 'pending_payment' && (
                    <div className="mt-2 btn-container">
                      <button
                        type="button"
                        className="btn btn-blue transition-transform duration-200 hover:scale-105"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPaymentPanel(req.id);
                          setPaymentMethod('');
                          setClientPhone('');
                        }}
                        disabled={proceedToCheckoutMutation.isLoading}
                      >
                        {proceedToCheckoutMutation.isLoading && proceedToCheckoutMutation.variables?.orderId === req.order?.id ? (
                          <>
                            <span className="spinner"></span> Proceeding...
                          </>
                        ) : (
                          'Proceed to Checkout'
                        )}
                      </button>
                      {req.order.payment_method === 'cash' && (
                        <button
                          type="button"
                          className="btn btn-yellow transition-transform duration-200 hover:scale-105"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowPaymentPanel(req.id);
                            setCheckoutAmount('');
                          }}
                          disabled={confirmCashMutation.isLoading}
                        >
                          {confirmCashMutation.isLoading && confirmCashMutation.variables?.orderId === req.order?.id ? (
                            <>
                              <span className="spinner"></span> Confirming...
                            </>
                          ) : (
                            'Confirm Cash Paid'
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  {showPaymentPanel === req.id && (
                    <div className="payment-panel open">
                      <h2 className="text-xl font-semibold mb-2">
                        {req.order?.payment_method === 'cash' && isProviderSection
                          ? 'Confirm Cash Received'
                          : req.order?.payment_method === 'cash'
                          ? 'Confirm Cash Paid'
                          : req.order?.payment_method === 'mpesa'
                          ? 'Confirm M-Pesa Payment'
                          : 'Proceed to Checkout'}
                      </h2>
                      {req.order?.payment_method === 'cash' ? (
                        <>
                          <p className="mb-2">
                            Confirm you {isProviderSection ? 'received' : 'paid'} {req.order.amount} KES in cash.
                          </p>
                          <form noValidate onSubmit={(e) => e.preventDefault()}>
                            <input
                              type="number"
                              inputMode="decimal"
                              value={checkoutAmount}
                              onChange={(e) => {
                                e.stopPropagation();
                                setCheckoutAmount(e.target.value);
                              }}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter')
                                  handleConfirmCash(req.order.id, isProviderSection ? 'provider' : 'client', checkoutAmount, req.id);
                              }}
                              onFocus={(e) => e.target.select()}
                              placeholder={`Enter ${req.order.amount} KES`}
                              className="input mb-2 text-sm"
                              min="0.01"
                              step="0.01"
                              autoComplete="off"
                            />
                          </form>
                          <div className="btn-container mt-2">
                            <button
                              type="button"
                              className="btn btn-green flex-1 text-sm transition-transform duration-200 hover:scale-105"
                              onClick={(e) => {
                                e.preventDefault();
                                handleConfirmCash(req.order.id, isProviderSection ? 'provider' : 'client', checkoutAmount, req.id);
                              }}
                              disabled={!checkoutAmount || confirmCashMutation.isLoading}
                            >
                              {confirmCashMutation.isLoading && confirmCashMutation.variables?.orderId === req.order.id ? (
                                <>
                                  <span className="spinner"></span> Confirming...
                                </>
                              ) : (
                                'Confirm'
                              )}
                            </button>
                            <button
                              type="button"
                              className="btn btn-red flex-1 text-sm transition-transform duration-200 hover:scale-105"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowPaymentPanel(null);
                                setCheckoutAmount('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : req.order?.payment_method === 'mpesa' ? (
                        <>
                          <p className="mb-2 font-bold text-lg">
                            Pay {req.order.amount} KES to SErraND Paybill: <span className="text-blue-300">4166063</span>
                          </p>
                          <p className="mb-2">
                            <strong>Payment Breakdown:</strong>
                          </p>
                          <ul className="list-disc ml-6 mb-2">
                            <li>Order Amount: {req.order.amount} KES</li>
                            <li>SErraND Commission: {(req.order.amount * 0.1).toFixed(2)} KES (10%)</li>
                            <li>Gross Payout: {(req.order.amount * 0.9).toFixed(2)} KES (90%)</li>
                            {b2cTx && (
                              <>
                                <li>B2C Fee Share: {b2cTx.b2c_fee ? b2cTx.b2c_fee.toFixed(2) : '0.00'} KES (50% of B2C fee)</li>
                                <li>Net Payout: {b2cTx.amount ? b2cTx.amount.toFixed(2) : '0.00'} KES</li>
                              </>
                            )}
                          </ul>
                          <p className="mb-2">
                            Enter your M-Pesa phone number and click "Initiate STK Push" to receive a payment prompt.
                          </p>
                          <form noValidate onSubmit={(e) => e.preventDefault()}>
                            <input
                              type="text"
                              value={clientPhone}
                              onChange={(e) => {
                                e.stopPropagation();
                                setClientPhone(e.target.value);
                              }}
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter') handleInitiateStkPush(req.order.id, req.id);
                              }}
                              onFocus={(e) => e.target.select()}
                              placeholder="Phone Number (0712345678 or 0112345678)"
                              className="input mb-2 text-sm"
                              autoComplete="off"
                            />
                          </form>
                          <div className="btn-container mt-2">
                            <button
                              type="button"
                              className="btn btn-green flex-1 text-sm transition-transform duration-200 hover:scale-105"
                              onClick={(e) => {
                                e.preventDefault();
                                handleInitiateStkPush(req.order.id, req.id);
                              }}
                              disabled={!clientPhone || initiateStkPushMutation.isLoading}
                            >
                              {initiateStkPushMutation.isLoading && initiateStkPushMutation.variables?.orderId === req.order.id ? (
                                <>
                                  <span className="spinner"></span> Initiating...
                                </>
                              ) : (
                                'Initiate STK Push'
                              )}
                            </button>
                            
                            <button
                              type="button"
                              className="btn btn-red flex-1 text-sm transition-transform duration-200 hover:scale-105"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowPaymentPanel(null);
                                setPaymentMethod('');
                                setClientPhone('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <form noValidate onSubmit={(e) => e.preventDefault()}>
                            <select
                              value={paymentMethod}
                              onChange={(e) => setPaymentMethod(e.target.value)}
                              className="input mb-2 text-sm"
                            >
                              <option value="">Select Payment Method</option>
                              <option value="mpesa">M-Pesa</option>
                              <option value="cash">Cash</option>
                            </select>
                          </form>
                          <div className="btn-container mt-2">
                            <button
                              type="button"
                              className="btn btn-green flex-1 text-sm transition-transform duration-200 hover:scale-105"
                              onClick={(e) => {
                                e.preventDefault();
                                handleProceedToCheckout(req.id, req.order.id);
                              }}
                              disabled={!paymentMethod}
                            >
                              Submit
                            </button>
                            <button
                              type="button"
                              className="btn btn-red flex-1 text-sm transition-transform duration-200 hover:scale-105"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowPaymentPanel(null);
                                setPaymentMethod('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {!isProviderSection && req.order && req.order.status === 'completed' && !req.order.rating && (
                    <div className="mt-2 rating-form-container">
                      <RatingForm
                        orderId={req.order.id}
                        onSuccess={() => {
                          queryClient.invalidateQueries(['clientOrders']);
                          queryClient.invalidateQueries(['providerOrders']);
                          toast.success('Rating submitted!');
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            <Pagination currentPage={currentPage} totalItems={filteredOrders.length} onPageChange={setCurrentPage} />
          </>
        ) : (
          <p>No matching orders found.</p>
        )}
      </div>
    );
  }
);

// ProgressTimer component
const ProgressTimer = ({ requestId }) => {
  const [countdown, setCountdown] = useState('');
  const [isWarning, setIsWarning] = useState(false);
  const [isOverdue, setIsOverdue] = useState(false);

  const { data: progressData } = useQuery({
    queryKey: ['orderProgress', requestId],
    queryFn: () => fetchOrderProgress(requestId),
    enabled: !!requestId,
    refetchInterval: 60000,
  });

  useEffect(() => {
    if (!progressData || progressData.status !== 'active') {
      setCountdown('');
      setIsWarning(false);
      setIsOverdue(false);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const isOverdue = progressData.is_overdue;
      const startTime = new Date(progressData.active_at);
      const lastUpdated = progressData.last_updated ? new Date(progressData.last_updated) : startTime;
      const deadline = new Date(lastUpdated.getTime() + 24 * 60 * 60 * 1000);
      const warningTime = new Date(lastUpdated.getTime() + 20 * 60 * 60 * 1000);
      const timeRemaining = deadline - now;

      if (isOverdue) {
        setCountdown('Overdue');
        setIsOverdue(true);
        setIsWarning(true);
      } else if (timeRemaining <= 0) {
        setCountdown('Overdue');
        setIsOverdue(true);
        setIsWarning(true);
      } else {
        const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
        const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        setCountdown(`${hours}h ${minutes}m`);
        setIsOverdue(false);
        setIsWarning(now >= warningTime);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [progressData]);

  if (!countdown) return null;

  return (
    <p>
      Next confirmation due in{' '}
      <span className={isOverdue ? 'text-red-500' : isWarning ? 'text-yellow-500' : 'text-green-500'}>
        {countdown}
      </span>
      {isWarning && !isOverdue && (
        <span className="text-yellow-500 ml-2">Confirm within 4 hours to avoid cancellation!</span>
      )}
      {isOverdue && (
        <span className="text-red-500 ml-2">Order overdue; awaiting cancellation.</span>
      )}
    </p>
  );
};

// Main ManageOrders component
function ManageOrders() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orderRefs = useRef({});
  const [error, setError] = useState('');
  const [statusSocket, setStatusSocket] = useState(null);
  const [debtSocket, setDebtSocket] = useState(null);
  const [showCheckoutPanel, setShowCheckoutPanel] = useState(null);
  const [showPaymentPanel, setShowPaymentPanel] = useState(null);
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const [checkoutAmount, setCheckoutAmount] = useState('');
  const [providerPhone, setProviderPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [pendingActiveIncomingPage, setPendingActiveIncomingPage] = useState(1);
  const [completedIncomingPage, setCompletedIncomingPage] = useState(1);
  const [pendingActiveOutgoingPage, setPendingActiveOutgoingPage] = useState(1);
  const [completedOutgoingPage, setCompletedOutgoingPage] = useState(1);
  const [pendingActiveIncomingFilters, setPendingActiveIncomingFilters] = useState({
    statuses: [],
    search: '',
    searchInput: {},
  });
  const [completedIncomingFilters, setCompletedIncomingFilters] = useState({
    statuses: [],
    search: '',
    searchInput: {},
  });
  const [pendingActiveOutgoingFilters, setPendingActiveOutgoingFilters] = useState({
    statuses: [],
    search: '',
    searchInput: {},
  });
  const [completedOutgoingFilters, setCompletedOutgoingFilters] = useState({
    statuses: [],
    search: '',
    searchInput: {},
  });
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Added for hamburger menu

  const { data: user, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/api/profile/`, { withCredentials: true });
      return response.data;
    },
    retry: false,
  });

  const { data: providerOrdersRaw, isLoading: providerLoading, error: providerError, refetch: refetchProviderOrders } = useQuery({
    queryKey: ['providerOrders'],
    queryFn: fetchOrders,
    enabled: !!user?.is_service_provider,
    retry: 2,
    staleTime: 1000 * 60,
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to load incoming orders.');
      toast.error('Failed to load incoming orders.');
    },
    onSuccess: (data) => {
      console.log('Provider Orders Raw:', data);
    },
  });

  const { data: clientOrdersRaw, isLoading: clientLoading, refetch: refetchClientOrders } = useQuery({
    queryKey: ['clientOrders'],
    queryFn: fetchClientOrders,
    enabled: !!user,
    staleTime: 1000 * 60,
    onError: () => toast.error('Failed to load outgoing orders.'),
    onSuccess: (data) => {
      console.log('Client Orders Raw:', data);
    },
  });

  // Transform provider orders to match expected structure
  const providerOrders = useMemo(() => {
    if (!providerOrdersRaw) return null;
    const incoming_requests = [
      ...(providerOrdersRaw.pending_active_incoming || []),
      ...(providerOrdersRaw.completed_incoming || []),
      ...(providerOrdersRaw.rejected_voided_incoming || []),
    ];
    const outgoing_requests = [
      ...(providerOrdersRaw.pending_active_outgoing || []),
      ...(providerOrdersRaw.completed_outgoing || []),
      ...(providerOrdersRaw.rejected_voided_outgoing || []),
    ];
    return {
      incoming_requests,
      outgoing_requests,
      total_debt: providerOrdersRaw.total_debt,
      can_accept_orders: providerOrdersRaw.can_accept_orders,
      debt_message: providerOrdersRaw.debt_message,
      trust_score: providerOrdersRaw.trust_score,
      trust_tier: providerOrdersRaw.trust_tier,
      warning_count: providerOrdersRaw.warning_count,
      is_restricted: providerOrdersRaw.is_restricted,
    };
  }, [providerOrdersRaw]);

  // Transform client orders to match expected structure
  const clientOrders = useMemo(() => {
    if (!clientOrdersRaw) return null;
    const service_requests = [
      ...(clientOrdersRaw.pending_active_requests || []),
      ...(clientOrdersRaw.completed_requests || []),
      ...(clientOrdersRaw.rejected_voided_requests || []),
    ];
    return { service_requests };
  }, [clientOrdersRaw]);

  const acceptMutation = useMutation({
    mutationFn: async (requestId) => {
      console.log(`Attempting to accept request ID: ${requestId}`);
      const response = await axios.post(`${API_URL}/api/request/${requestId}/accept/`, {}, { withCredentials: true });
      return response.data;
    },
    onMutate: async (requestId) => {
      await queryClient.cancelQueries(['providerOrders', 'clientOrders']);
      const previousProviderOrders = queryClient.getQueryData(['providerOrders']);
      const previousClientOrders = queryClient.getQueryData(['clientOrders']);
      queryClient.setQueryData(['providerOrders'], (old) => {
        if (!old || !old.incoming_requests) return old;
        return {
          ...old,
          incoming_requests: old.incoming_requests.map((req) =>
            req.id === requestId ? { ...req, status: 'scheduled' } : req
          ),
        };
      });
      queryClient.setQueryData(['clientOrders'], (old) => {
        if (!old || !old.service_requests) return old;
        return {
          ...old,
          service_requests: old.service_requests.map((req) =>
            req.id === requestId ? { ...req, status: 'scheduled' } : req
          ),
        };
      });
      return { previousProviderOrders, previousClientOrders };
    },
    onSuccess: (data, requestId) => {
      console.log(`Request ${requestId} accepted successfully:`, data);
      toast.success('Request accepted! Awaiting scheduling confirmation.', { duration: 5000 });
      if (orderRefs.current[requestId]) {
        orderRefs.current[requestId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    onError: (err, requestId, context) => {
      queryClient.setQueryData(['providerOrders'], context.previousProviderOrders);
      queryClient.setQueryData(['clientOrders'], context.previousClientOrders);
      toast.error(err.response?.data?.error || 'Failed to accept request.');
    },
    onSettled: () => {
      queryClient.invalidateQueries(['providerOrders']);
      queryClient.invalidateQueries(['clientOrders']);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (requestId) => {
      console.log(`Attempting to reject request ID: ${requestId}`);
      const response = await axios.post(`${API_URL}/api/request/${requestId}/reject/`, {}, { withCredentials: true });
      return response.data;
    },
    onMutate: async (requestId) => {
      await queryClient.cancelQueries(['providerOrders', 'clientOrders']);
      const previousProviderOrders = queryClient.getQueryData(['providerOrders']);
      const previousClientOrders = queryClient.getQueryData(['clientOrders']);
      queryClient.setQueryData(['providerOrders'], (old) => {
        if (!old || !old.incoming_requests) return old;
        return {
          ...old,
          incoming_requests: old.incoming_requests.map((req) =>
            req.id === requestId ? { ...req, status: 'rejected' } : req
          ),
        };
      });
      queryClient.setQueryData(['clientOrders'], (old) => {
        if (!old || !old.service_requests) return old;
        return {
          ...old,
          service_requests: old.service_requests.map((req) =>
            req.id === requestId ? { ...req, status: 'rejected' } : req
          ),
        };
      });
      return { previousProviderOrders, previousClientOrders };
    },
    onSuccess: (data, requestId) => {
      console.log(`Request ${requestId} rejected successfully:`, data);
      toast.success('Request rejected.', { duration: 5000 });
      if (orderRefs.current[requestId]) {
        orderRefs.current[requestId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    onError: (err, requestId, context) => {
      queryClient.setQueryData(['providerOrders'], context.previousProviderOrders);
      queryClient.setQueryData(['clientOrders'], context.previousClientOrders);
      toast.error(err.response?.data?.error || 'Failed to reject request.');
    },
    onSettled: () => {
      queryClient.invalidateQueries(['providerOrders']);
      queryClient.invalidateQueries(['clientOrders']);
    },
  });

  const initiateCheckoutMutation = useMutation({
    mutationFn: async ({ requestId, amount, provider_phone }) => {
      console.log(`Attempting to initiate checkout for request ID: ${requestId}`);
      const response = await axios.post(
        `${API_URL}/api/request/${requestId}/checkout/`,
        { amount, provider_phone },
        { withCredentials: true }
      );
      return response.data;
    },
    onMutate: async ({ requestId }) => {
      console.log(`Optimistic update for initiating checkout ID: ${requestId}`);
      await queryClient.cancelQueries(['providerOrders', 'clientOrders']);
      const previousProviderOrders = queryClient.getQueryData(['providerOrders']);
      const previousClientOrders = queryClient.getQueryData(['clientOrders']);
    
      // Update providerOrders
      queryClient.setQueryData(['providerOrders'], (old) => {
        if (!old || !old.incoming_requests) return old;
        return {
          ...old,
          incoming_requests: old.incoming_requests.map((req) =>
            req.id === requestId
              ? { ...req, status: 'checkout_initiated', order: { ...req.order, status: 'checkout_initiated' } }
              : req
          ),
        };
      });
    
      // Update clientOrders to reflect checkout_initiated for the client
      queryClient.setQueryData(['clientOrders'], (old) => {
        if (!old || !old.service_requests) return old;
        return {
          ...old,
          service_requests: old.service_requests.map((req) =>
            req.id === requestId
              ? { ...req, status: 'checkout_initiated', order: { ...req.order, status: 'checkout_initiated' } }
              : req
          ),
        };
      });
    
      return { previousProviderOrders, previousClientOrders };
    },
    onSuccess: (data, variables) => {
      console.log(`Checkout initiated for request ${variables.requestId}:`, data);
      toast.success('Checkout initiated! Client, please proceed to checkout.', { duration: 5000 });
      setShowCheckoutPanel(null);
      setCheckoutAmount('');
      setProviderPhone('');
      if (orderRefs.current[variables.requestId]) {
        orderRefs.current[variables.requestId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    onError: (err, variables, context) => {
      console.error(`Failed to initiate checkout for request ${variables.requestId}:`, err.response?.data || err.message);
      queryClient.setQueryData(['providerOrders'], context.previousProviderOrders);
      queryClient.setQueryData(['clientOrders'], context.previousClientOrders);
      toast.error(err.response?.data?.error || 'Failed to initiate checkout.');
    },
    onSettled: () => {
      queryClient.invalidateQueries(['providerOrders']);
      queryClient.invalidateQueries(['clientOrders']);
    },
  });
  const proceedToCheckoutMutation = useMutation({
    mutationFn: async ({ orderId, paymentMethod }) => {
      console.log(`Attempting to proceed to checkout for order ID: ${orderId}`);
      const response = await axios.post(
        `${API_URL}/api/order/${orderId}/payment/`,
        { payment_method: paymentMethod.toLowerCase() },
        { withCredentials: true }
      );
      return response.data;
    },
    onMutate: async ({ orderId, paymentMethod }) => {
      console.log(`Optimistic update for proceeding to checkout ID: ${orderId}`);
      await queryClient.cancelQueries(['clientOrders', 'providerOrders']);
      const previousClientOrders = queryClient.getQueryData(['clientOrders']);
      const previousProviderOrders = queryClient.getQueryData(['providerOrders']);
      queryClient.setQueryData(['clientOrders'], (old) => {
        if (!old || !old.service_requests) return old;
        return {
          ...old,
          service_requests: old.service_requests.map((req) =>
            req.order && req.order.id === orderId
              ? { ...req, order: { ...req.order, status: 'pending_payment', payment_method: paymentMethod.toLowerCase() } }
              : req
          ),
        };
      });
      queryClient.setQueryData(['providerOrders'], (old) => {
        if (!old || !old.incoming_requests) return old;
        return {
          ...old,
          incoming_requests: old.incoming_requests.map((req) =>
            req.order && req.order.id === orderId
              ? { ...req, order: { ...req.order, status: 'pending_payment', payment_method: paymentMethod.toLowerCase() } }
              : req
          ),
        };
      });
      return { previousClientOrders, previousProviderOrders };
    },
    onSuccess: (data, variables) => {
      console.log(`Proceeded to checkout for order ${variables.orderId}:`, data);
      if (data.success) {
        toast.success(
          variables.paymentMethod.toLowerCase() === 'mpesa'
            ? 'Payment method selected! Enter your phone number to initiate M-Pesa payment.'
            : 'Cash payment selected! Confirm the amount paid.',
          { duration: 5000 }
        );
        setShowPaymentPanel(null);
        setPaymentMethod('');
        const requestId = clientOrders?.service_requests.find((req) => req.order?.id === variables.orderId)?.id;
        if (requestId && orderRefs.current[requestId]) {
          orderRefs.current[requestId].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        toast.error(`Payment failed: ${data.error}`);
      }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(['clientOrders'], context.previousClientOrders);
      queryClient.setQueryData(['providerOrders'], context.previousProviderOrders);
      toast.error(err.response?.data?.error || 'Failed to submit payment.');
    },
    onSettled: () => {
      queryClient.invalidateQueries(['clientOrders']);
      queryClient.invalidateQueries(['providerOrders']);
    },
  });

  const initiateStkPushMutation = useMutation({
    mutationFn: async ({ orderId, phone_number }) => {
      console.log(`Attempting to initiate STK Push for order ID: ${orderId}`);
      const response = await axios.post(
        `${API_URL}/api/order/${orderId}/stk_push/`,
        { phone_number },
        { withCredentials: true }
      );
      return response.data;
    },
    onMutate: async ({ orderId }) => {
      console.log(`Optimistic update for initiating STK Push for order ID: ${orderId}`);
      await queryClient.cancelQueries(['clientOrders', 'providerOrders']);
      const previousClientOrders = queryClient.getQueryData(['clientOrders']);
      const previousProviderOrders = queryClient.getQueryData(['providerOrders']);
      queryClient.setQueryData(['clientOrders'], (old) => {
        if (!old || !old.service_requests) return old;
        return {
          ...old,
          service_requests: old.service_requests.map((req) =>
            req.order && req.order.id === orderId ? { ...req, order: { ...req.order, status: 'processing' } } : req
          ),
        };
      });
      queryClient.setQueryData(['providerOrders'], (old) => {
        if (!old || !old.incoming_requests) return old;
        return {
          ...old,
          incoming_requests: old.incoming_requests.map((req) =>
            req.order && req.order.id === orderId ? { ...req, order: { ...req.order, status: 'processing' } } : req
          ),
        };
      });
      return { previousClientOrders, previousProviderOrders };
    },
    onSuccess: (data, variables) => {
      console.log(`STK Push initiated for order ${variables.orderId}:`, data);
      toast.success('STK Push initiated! Check your phone for the M-Pesa prompt.', { duration: 5000 });
      setShowPaymentPanel(null);
      setClientPhone('');
      const requestId = clientOrders?.service_requests.find((req) => req.order?.id === variables.orderId)?.id;
      if (requestId && orderRefs.current[requestId]) {
        orderRefs.current[requestId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    onError: async (err, { orderId }, context) => {
      console.error(`Failed to initiate STK Push for order ${orderId}:`, err.response?.data || err.message);
      queryClient.setQueryData(['clientOrders'], context.previousClientOrders);
      queryClient.setQueryData(['providerOrders'], context.previousProviderOrders);
      try {
        const response = await axios.get(`${API_URL}/api/client_orders/`, { withCredentials: true });
        const order = response.data.service_requests.find((req) => req.order?.id === orderId)?.order;
        if (order && ['pending_payment', 'processing'].includes(order.status)) {
          toast.error('Transaction not completed. Please try again.', { duration: 5000 });
        } else {
          toast.error('Transaction status updated. Please check the order.', { duration: 5000 });
          setShowPaymentPanel(null);
          setClientPhone('');
        }
      } catch (checkErr) {
        console.error(`Failed to check order status for ${orderId}:`, checkErr.response?.data || checkErr.message);
        toast.error('Failed to verify transaction status. Please try again.', { duration: 5000 });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['clientOrders']);
      queryClient.invalidateQueries(['providerOrders']);
    },
  });

  const confirmCashMutation = useMutation({
    mutationFn: async ({ orderId, role, amount }) => {
      const response = await axios.post(
        `${API_URL}/api/order/${orderId}/confirm_cash/`,
        { role, amount },
        { withCredentials: true }
      );
      return response.data;
    },
    onMutate: async ({ orderId, role }) => {
      console.log(`Optimistic update for confirming cash for order ID: ${orderId}`);
      await queryClient.cancelQueries(['clientOrders', 'providerOrders']);
      const previousClientOrders = queryClient.getQueryData(['clientOrders']);
      const previousProviderOrders = queryClient.getQueryData(['providerOrders']);
      const newStatus = role === 'client' ? 'pending_payment' : 'completed';
      queryClient.setQueryData(['clientOrders'], (old) => {
        if (!old || !old.service_requests) return old;
        return {
          ...old,
          service_requests: old.service_requests.map((req) =>
            req.order && req.order.id === orderId ? { ...req, order: { ...req.order, status: newStatus } } : req
          ),
        };
      });
      queryClient.setQueryData(['providerOrders'], (old) => {
        if (!old || !old.incoming_requests) return old;
        return {
          ...old,
          incoming_requests: old.incoming_requests.map((req) =>
            req.order && req.order.id === orderId ? { ...req, order: { ...req.order, status: newStatus } } : req
          ),
        };
      });
      return { previousClientOrders, previousProviderOrders };
    },
    onSuccess: (_, variables) => {
      toast.success(
        `${variables.role.charAt(0).toUpperCase() + variables.role.slice(1)} confirmed cash payment! ${
          variables.role === 'client' ? 'Awaiting provider confirmation.' : 'Order completed.'
        }`,
        { duration: 5000 }
      );
      setShowPaymentPanel(null);
      setCheckoutAmount('');
      const requestId = (variables.role === 'client' ? clientOrders : providerOrders)?.[
        variables.role === 'client' ? 'service_requests' : 'incoming_requests'
      ].find((req) => req.order?.id === variables.orderId)?.id;
      if (requestId && orderRefs.current[requestId]) {
        orderRefs.current[requestId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(['clientOrders'], context.previousClientOrders);
      queryClient.setQueryData(['providerOrders'], context.previousProviderOrders);
      toast.error(err.response?.data?.error || 'Confirmation failed.');
    },
    onSettled: () => {
      queryClient.invalidateQueries(['clientOrders']);
      queryClient.invalidateQueries(['providerOrders']);
    },
  });

  const handleAccept = useCallback(
    (requestId) => {
      console.log(`handleAccept called with requestId: ${requestId}`);
      console.log(`Accept button disabled conditions: can_accept_orders=${providerOrders?.can_accept_orders}, is_restricted=${providerOrders?.is_restricted}`);
      acceptMutation.mutate(requestId);
    },
    [acceptMutation, providerOrders?.can_accept_orders, providerOrders?.is_restricted]
  );

  const handleReject = useCallback(
    (requestId) => {
      console.log(`handleReject called with requestId: ${requestId}`);
      rejectMutation.mutate(requestId);
    },
    [rejectMutation]
  );

  const handleInitiateCheckout = useCallback(
    async (requestId) => {
      if (!checkoutAmount || isNaN(checkoutAmount) || checkoutAmount <= 0) {
        toast.error('Please enter a valid amount.');
        return;
      }
      if (!providerPhone || !/^0[0-9]{9}$/.test(providerPhone)) {
        toast.error('Please enter a valid phone number (e.g., 0712345678).');
        return;
      }
      try {
        const minimumCharge = await fetchServiceMinimumCharge(requestId);
        if (parseFloat(checkoutAmount) < minimumCharge) {
          toast.error(`Amount must be at least ${minimumCharge} KES for this service.`);
          return;
        }
        initiateCheckoutMutation.mutate({ requestId, amount: checkoutAmount, provider_phone: providerPhone });
      } catch (err) {
        toast.error('Failed to validate minimum charge. Please try again.');
      }
    },
    [checkoutAmount, providerPhone, initiateCheckoutMutation]
  );

  const handleProceedToCheckout = useCallback(
    (requestId, orderId) => {
      if (!paymentMethod || !['mpesa', 'cash'].includes(paymentMethod.toLowerCase())) {
        toast.error('Please select a valid payment method (M-Pesa or Cash).');
        return;
      }
      const order = clientOrders?.service_requests.find((req) => req.id === requestId)?.order;
      if (!order || order.id !== orderId) {
        toast.error('Order not found.');
        return;
      }
      if (order.status === 'completed') {
        toast.error('Order already completed.');
        return;
      }
      proceedToCheckoutMutation.mutate({ orderId, paymentMethod });
    },
    [paymentMethod, clientOrders, proceedToCheckoutMutation]
  );

  const handleInitiateStkPush = useCallback(
    (orderId, requestId) => {
      if (!clientPhone || !/^0[0-9]{9}$/.test(clientPhone)) {
        toast.error('Please enter a valid phone number (e.g., 0712345678 or 0112345678).');
        return;
      }
      const order = clientOrders?.service_requests.find((req) => req.id === requestId)?.order;
      if (!order || order.id !== orderId) {
        toast.error('Order not found.');
        return;
      }
      if (order.status === 'completed') {
        toast.error('Order already completed.');
        return;
      }
      initiateStkPushMutation.mutate({ orderId, phone_number: clientPhone });
    },
    [clientPhone, clientOrders, initiateStkPushMutation]
  );

  const handleConfirmCash = useCallback(
    (orderId, role, amount, requestId) => {
      const order = role === 'client'
        ? clientOrders?.service_requests.find((req) => req.id === requestId)?.order
        : providerOrders?.incoming_requests.find((req) => req.id === requestId)?.order;
      if (!order || order.id !== orderId) {
        toast.error('Order not found.');
        return;
      }
      if (order.status === 'completed') {
        toast.error('Order already completed.');
        return;
      }
      const parsedOrderAmount = parseFloat(order.amount).toFixed(2);
      const parsedConfirmedAmount = parseFloat(amount).toFixed(2);
      if (parsedConfirmedAmount !== parsedOrderAmount) {
        toast.error('Amount does not match. Please enter the correct amount.');
        return;
      }
      confirmCashMutation.mutate({ orderId, role, amount });
    },
    [clientOrders, providerOrders, confirmCashMutation]
  );

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
      toast.success('Logged out successfully.');
      setIsMenuOpen(false); // Close menu on logout
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout.');
    } finally {
      setIsAuthLoading(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const getStatusColor = useMemo(
    () => (status) => {
      switch (status) {
        case 'pending':
          return 'text-gray-500';
        case 'scheduled':
          return 'text-yellow-500';
        case 'active':
          return 'text-green-500';
        case 'checkout_initiated':
          return 'text-blue-300';
        case 'rejected':
          return 'text-red-500';
        case 'completed':
          return 'text-purple-500';
        case 'voided':
          return 'text-red-500';
        case 'cancelled':
          return 'text-red-500';
        case 'pending_payment':
          return 'text-blue-500';
        case 'processing':
          return 'text-blue-500';
        case 'success':
          return 'text-purple-500';
        case 'failed':
          return 'text-red-500';
        default:
          return 'text-white';
      }
    },
    []
  );

  const canInitiateCheckout = useCallback(
    (request) => {
      if (request.status !== 'active' || !request.active_at) return false;
      const activeTime = new Date(request.active_at);
      const now = new Date();
      const tenMinutesLater = new Date(activeTime.getTime() + 10 * 60 * 1000);
      return now >= tenMinutesLater && !request.is_overdue;
    },
    []
  );

  useEffect(() => {
    if (!user) return;
    // Handle foreground FCM notifications
    onMessage(messaging, (payload) => {
      console.log('FCM message received:', payload);
      const notification = payload.notification || {};
      const data = payload.data || {};
      const message = notification.body || data.message || 'New notification received';
      toast.success(message, {
        duration: 10000,
        position: 'top-right',
      });
      // Invalidate queries to refresh order data
      if (data.order_id) {
        queryClient.invalidateQueries(['clientOrders']);
        queryClient.invalidateQueries(['providerOrders']);
      }
    });

    const statusWs = new WebSocket(`${WS_URL}/ws/status/`);
    statusWs.onopen = () => {
        console.log('Status WebSocket connected');
    };
    statusWs.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('Status WebSocket message:', data);
        if (data.type === 'status_update') {
            queryClient.setQueryData(['providerOrders'], (old) => {
                if (!old) return old;
                const updateRequest = (req) =>
                    req.id.toString() === data.request_id.toString()
                        ? {
                              ...req,
                              status: data.status,
                              active_at: data.active_at || req.active_at,
                              order: req.order
                                  ? {
                                        ...req.order,
                                        status: data.order_status || req.order.status,
                                        disbursement_status: data.disbursement_status || req.order.disbursement_status,
                                    }
                                  : req.order,
                          }
                        : req;
                return {
                    ...old,
                    pending_active_incoming: old.pending_active_incoming.map(updateRequest),
                    completed_incoming: old.completed_incoming.map(updateRequest),
                    rejected_voided_incoming: old.rejected_voided_incoming.map(updateRequest),
                    pending_active_outgoing: old.pending_active_outgoing.map(updateRequest),
                    completed_outgoing: old.completed_outgoing.map(updateRequest),
                    rejected_voided_outgoing: old.rejected_voided_outgoing.map(updateRequest),
                };
            });
            queryClient.setQueryData(['clientOrders'], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    pending_active_requests: old.pending_active_requests.map((req) =>
                        req.id.toString() === data.request_id.toString()
                            ? {
                                  ...req,
                                  status: data.status,
                                  active_at: data.active_at || req.active_at,
                                  order: req.order
                                      ? {
                                            ...req.order,
                                            status: data.order_status || req.order.status,
                                            disbursement_status: data.disbursement_status || req.order.disbursement_status,
                                        }
                                      : req.order,
                              }
                            : req
                    ),
                    completed_requests: old.completed_requests.map((req) =>
                        req.id.toString() === data.request_id.toString()
                            ? {
                                  ...req,
                                  status: data.status,
                                  active_at: data.active_at || req.active_at,
                                  order: req.order
                                      ? {
                                            ...req.order,
                                            status: data.order_status || req.order.status,
                                            disbursement_status: data.disbursement_status || req.order.disbursement_status,
                                        }
                                      : req.order,
                              }
                            : req
                    ),
                    rejected_voided_requests: old.rejected_voided_requests.map((req) =>
                        req.id.toString() === data.request_id.toString()
                            ? {
                                  ...req,
                                  status: data.status,
                                  active_at: data.active_at || req.active_at,
                                  order: req.order
                                      ? {
                                            ...req.order,
                                            status: data.order_status || req.order.status,
                                            disbursement_status: data.disbursement_status || req.order.disbursement_status,
                                        }
                                      : req.order,
                              }
                            : req
                    ),
                };
            });
            if (data.order_status === 'completed') {
                toast.success(`Order ${data.request_id} completed!`, { duration: 10000 });
            }
            if (data.disbursement_status === 'success') {
                queryClient.invalidateQueries(['providerOrders']);
                queryClient.invalidateQueries(['clientOrders']);
            } else if (data.disbursement_status === 'failed') {
                toast.error(`Disbursement for Order ${data.request_id} failed.`, { duration: 10000 });
            }
            setPendingActiveIncomingPage(1);
            setCompletedIncomingPage(1);
            setPendingActiveOutgoingPage(1);
            setCompletedOutgoingPage(1);
        } else if (data.type === 'progress_confirmation') {
            queryClient.invalidateQueries(['orderProgress']);
            toast.success(`Progress confirmed for order #${data.request_id}`);
        } else if (data.type === 'warning_notification') {
            toast.error(data.message, { duration: 10000 });
        } else if (data.type === 'order_cancellation') {
            queryClient.setQueryData(['providerOrders'], (old) => {
                if (!old) return old;
                const updateRequest = (req) =>
                    req.id.toString() === data.request_id.toString()
                        ? {
                              ...req,
                              status: 'cancelled',
                              warning_count: data.warning_count,
                              is_restricted: data.warning_count >= 3,
                          }
                        : req;
                return {
                    ...old,
                    pending_active_incoming: old.pending_active_incoming.map(updateRequest),
                    completed_incoming: old.completed_incoming.map(updateRequest),
                    rejected_voided_incoming: old.rejected_voided_incoming.map(updateRequest),
                    pending_active_outgoing: old.pending_active_outgoing.map(updateRequest),
                    completed_outgoing: old.completed_outgoing.map(updateRequest),
                    rejected_voided_outgoing: old.rejected_voided_outgoing.map(updateRequest),
                    is_restricted: old.is_restricted || data.warning_count >= 3,
                    warning_count: Math.max(old.warning_count || 0, data.warning_count),
                };
            });
            queryClient.setQueryData(['clientOrders'], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    pending_active_requests: old.pending_active_requests.map((req) =>
                        req.id.toString() === data.request_id.toString() ? { ...req, status: 'cancelled' } : req
                    ),
                    completed_requests: old.completed_requests.map((req) =>
                        req.id.toString() === data.request_id.toString() ? { ...req, status: 'cancelled' } : req
                    ),
                    rejected_voided_requests: old.rejected_voided_requests.map((req) =>
                        req.id.toString() === data.request_id.toString() ? { ...req, status: 'cancelled' } : req
                    ),
                };
            });
            toast.error(data.message, { duration: 10000 });
        }
    };
    statusWs.onclose = () => {
        console.log('Status WebSocket closed, reconnecting in 5s');
        setTimeout(() => setStatusSocket(new WebSocket(`${WS_URL}/ws/status/`)), 5000);
    };
    statusWs.onerror = (error) => console.error('Status WebSocket error:', error);
    setStatusSocket(statusWs);

    const debtWs = new WebSocket(`${WS_URL}/ws/debt/`);
    debtWs.onopen = () => debtWs.send('{}');
    debtWs.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.total_debt !== undefined) {
        queryClient.setQueryData(['providerOrders'], (old) => {
          if (!old) return old;
          return {
            ...old,
            total_debt: data.total_debt,
            can_accept_orders: data.total_debt <= 500,
            debt_message:
              data.total_debt > 500
                ? `Cannot accept new orders: debt (${data.total_debt} KES) exceeds threshold (500 KES)`
                : null,
          };
        });
        queryClient.setQueryData(['user'], (old) => {
          if (!old) return old;
          return { ...old, total_debt: data.total_debt };
        });
      } else if (data.error) {
        setError(data.error);
        toast.error(data.error);
      }
    };
    debtWs.onclose = () => setTimeout(() => setDebtSocket(new WebSocket(`${WS_URL}/ws/debt/`)), 5000);
    debtWs.onerror = (error) => console.error('Debt WebSocket error:', error);
    setDebtSocket(debtWs);

    return () => {
      if (statusWs) statusWs.close();
      if (debtWs) debtWs.close();
    };
  }, [user, queryClient]);

  useEffect(() => {
    const statusPanelRef = document.querySelector('.status-panel');
    if (showStatusPanel && statusPanelRef.current) {
      statusPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showStatusPanel]);

  if (userLoading) return <div className="text-center text-white py-12">Loading user data...</div>;

  if (!user || userError) {
    return (
      <div className="orders-container flex items-center justify-center min-h-screen">
        <div className="order-card text-center animate-fade-in">
          <h2 className="text-2xl font-semibold mb-4">Sorry!</h2>
          <p className="mb-6">This page is only available to users with accounts.</p>
          <div className="flex justify-center gap-4">
            <button
              className="btn btn-blue transition-transform duration-200 hover:scale-105"
              onClick={() => navigate('/', { state: { showLogin: true } })}
            >
              Log In
            </button>
            <button
              className="btn btn-green transition-transform duration-200 hover:scale-105"
              onClick={() => navigate('/', { state: { showRegister: true } })}
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isProvider = !!user?.is_service_provider;

  return (
    <div className="orders-container">
      <nav className="navbar">
        <div className="navbar-container">
          <Link to="/" className="logo-container nav-logo">
            <span className="se logo-se">SE</span>
            <span className="middle logo-middle">RRA</span>
            <span className="nd logo-nd">ND</span>
          </Link>

          {/* Hamburger Icon for Mobile */}
          <button
            className="md:hidden hamburger"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          </button>

          {/* Navbar Links for Desktop/Tablet */}
          <div className="hidden md:flex md:gap-4">
            <Link to="/" className="btn btn-primary">Home</Link>
            <Link to="/profile" className="btn">Profile</Link>
            <Link to="/contact" className="btn">Contact</Link>
            <Link to="/search" className="btn">Search Providers</Link>
            {userLoading || isAuthLoading ? (
              <span className="text-gray-400 self-center">Loading...</span>
            ) : user?.username ? (
              <>
                <span className="welcome-msg">Jambo, {user.username}!</span>
                <button onClick={handleLogout} className="btn btn-red">Logout</button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/', { state: { showLogin: true } })}
                  className="btn"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/', { state: { showRegister: true } })}
                  className="btn btn-green"
                >
                  Register
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Overlay */}
          {isMenuOpen && (
            <div className="mobile-menu-overlay" onClick={closeMenu}></div>
          )}

          {/* Mobile Menu */}
          <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
            <button
              className="mobile-menu-close"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <span className="close-icon">×</span>
            </button>
            <div className="mobile-menu-header">
              <Link to="/" className="logo-container nav-logo" onClick={closeMenu}>
                <span className="se logo-se">SE</span>
                <span className="middle logo-middle">RRA</span>
                <span className="nd logo-nd">ND</span>
              </Link>
            </div>
            <Link to="/" className="mobile-menu-item" onClick={closeMenu}>Home</Link>
            <Link to="/profile" className="mobile-menu-item" onClick={closeMenu}>Profile</Link>
            <Link to="/contact" className="mobile-menu-item" onClick={closeMenu}>Contact</Link>
            <Link to="/search" className="mobile-menu-item" onClick={closeMenu}>Search Providers</Link>
            {userLoading || isAuthLoading ? (
              <span className="mobile-menu-item text-gray-400">Loading...</span>
            ) : user?.username ? (
              <>
                <span className="mobile-menu-item welcome-msg">Jambo, {user.username}!</span>
                <button onClick={handleLogout} className="mobile-menu-item btn btn-red" disabled={isAuthLoading}>
                  {isAuthLoading ? 'Logging out...' : 'Logout'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    navigate('/', { state: { showLogin: true } });
                    closeMenu();
                  }}
                  className="mobile-menu-item btn"
                >
                  Login
                </button>
                <button
                  onClick={() => {
                    navigate('/', { state: { showRegister: true } });
                    closeMenu();
                  }}
                  className="mobile-menu-item btn btn-green"
                >
                  Register
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <div className="orders-content">
        {error && <div className="text-red-500 text-center mb-4">{error}</div>}
        {user?.is_service_provider && providerOrders && (
          <>
            <OrderSection
              title="Pending/Active Incoming Orders"
              orders={providerOrders.incoming_requests.filter((req) =>
                ['pending', 'scheduled', 'active', 'checkout_initiated', 'pending_payment'].includes(req.status)
              )}
              filters={pendingActiveIncomingFilters}
              setFilters={setPendingActiveIncomingFilters}
              currentPage={pendingActiveIncomingPage}
              setCurrentPage={setPendingActiveIncomingPage}
              isCompleted={false}
              isProviderSection={true}
              sectionKey="pendingActiveIncoming"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              handleInitiateCheckout={handleInitiateCheckout}
              handleProceedToCheckout={handleProceedToCheckout}
              handleConfirmCash={handleConfirmCash}
              handleInitiateStkPush={handleInitiateStkPush}
              getStatusColor={getStatusColor}
              canInitiateCheckout={canInitiateCheckout}
              showCheckoutPanel={showCheckoutPanel}
              setShowCheckoutPanel={setShowCheckoutPanel}
              showPaymentPanel={showPaymentPanel}
              setShowPaymentPanel={setShowPaymentPanel}
              checkoutAmount={checkoutAmount}
              setCheckoutAmount={setCheckoutAmount}
              providerPhone={providerPhone}
              setProviderPhone={setProviderPhone}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              clientPhone={clientPhone}
              setClientPhone={setClientPhone}
              queryClient={queryClient}
              refetchClientOrders={refetchClientOrders}
              refetchProviderOrders={refetchProviderOrders}
              user={user}
              orderRefs={orderRefs}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              initiateCheckoutMutation={initiateCheckoutMutation}
              proceedToCheckoutMutation={proceedToCheckoutMutation}
              confirmCashMutation={confirmCashMutation}
              initiateStkPushMutation={initiateStkPushMutation}
            />
            <OrderSection
              title="Completed/Rejected Incoming Orders"
              orders={providerOrders.incoming_requests.filter((req) =>
                ['completed', 'rejected', 'voided', 'cancelled'].includes(req.status)
              )}
              filters={completedIncomingFilters}
              setFilters={setCompletedIncomingFilters}
              currentPage={completedIncomingPage}
              setCurrentPage={setCompletedIncomingPage}
              isCompleted={true}
              isProviderSection={true}
              sectionKey="completedIncoming"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              handleInitiateCheckout={handleInitiateCheckout}
              handleProceedToCheckout={handleProceedToCheckout}
              handleConfirmCash={handleConfirmCash}
              handleInitiateStkPush={handleInitiateStkPush}
              getStatusColor={getStatusColor}
              canInitiateCheckout={canInitiateCheckout}
              showCheckoutPanel={showCheckoutPanel}
              setShowCheckoutPanel={setShowCheckoutPanel}
              showPaymentPanel={showPaymentPanel}
              setShowPaymentPanel={setShowPaymentPanel}
              checkoutAmount={checkoutAmount}
              setCheckoutAmount={setCheckoutAmount}
              providerPhone={providerPhone}
              setProviderPhone={setProviderPhone}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              clientPhone={clientPhone}
              setClientPhone={setClientPhone}
              queryClient={queryClient}
              refetchClientOrders={refetchClientOrders}
              refetchProviderOrders={refetchProviderOrders}
              user={user}
              orderRefs={orderRefs}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              initiateCheckoutMutation={initiateCheckoutMutation}
              proceedToCheckoutMutation={proceedToCheckoutMutation}
              confirmCashMutation={confirmCashMutation}
              initiateStkPushMutation={initiateStkPushMutation}
            />
          </>
        )}
        {clientOrders && (
          <>
            <OrderSection
              title="Pending/Active Outgoing Orders"
              orders={clientOrders.service_requests.filter((req) =>
                ['pending', 'scheduled', 'active', 'checkout_initiated', 'pending_payment'].includes(req.status)
              )}
              filters={pendingActiveOutgoingFilters}
              setFilters={setPendingActiveOutgoingFilters}
              currentPage={pendingActiveOutgoingPage}
              setCurrentPage={setPendingActiveOutgoingPage}
              isCompleted={false}
              isProviderSection={false}
              sectionKey="pendingActiveOutgoing"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              handleInitiateCheckout={handleInitiateCheckout}
              handleProceedToCheckout={handleProceedToCheckout}
              handleConfirmCash={handleConfirmCash}
              handleInitiateStkPush={handleInitiateStkPush}
              getStatusColor={getStatusColor}
              canInitiateCheckout={canInitiateCheckout}
              showCheckoutPanel={showCheckoutPanel}
              setShowCheckoutPanel={setShowCheckoutPanel}
              showPaymentPanel={showPaymentPanel}
              setShowPaymentPanel={setShowPaymentPanel}
              checkoutAmount={checkoutAmount}
              setCheckoutAmount={setCheckoutAmount}
              providerPhone={providerPhone}
              setProviderPhone={setProviderPhone}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              clientPhone={clientPhone}
              setClientPhone={setClientPhone}
              queryClient={queryClient}
              refetchClientOrders={refetchClientOrders}
              refetchProviderOrders={refetchProviderOrders}
              user={user}
              orderRefs={orderRefs}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              initiateCheckoutMutation={initiateCheckoutMutation}
              proceedToCheckoutMutation={proceedToCheckoutMutation}
              confirmCashMutation={confirmCashMutation}
              initiateStkPushMutation={initiateStkPushMutation}
            />
            <OrderSection
              title="Completed/Rejected Outgoing Orders"
              orders={clientOrders.service_requests.filter((req) =>
                ['completed', 'rejected', 'voided', 'cancelled'].includes(req.status)
              )}
              filters={completedOutgoingFilters}
              setFilters={setCompletedOutgoingFilters}
              currentPage={completedOutgoingPage}
              setCurrentPage={setCompletedOutgoingPage}
              isCompleted={true}
              isProviderSection={false}
              sectionKey="completedOutgoing"
              providerOrders={providerOrders}
              handleAccept={handleAccept}
              handleReject={handleReject}
              handleInitiateCheckout={handleInitiateCheckout}
              handleProceedToCheckout={handleProceedToCheckout}
              handleConfirmCash={handleConfirmCash}
              handleInitiateStkPush={handleInitiateStkPush}
              getStatusColor={getStatusColor}
              canInitiateCheckout={canInitiateCheckout}
              showCheckoutPanel={showCheckoutPanel}
              setShowCheckoutPanel={setShowCheckoutPanel}
              showPaymentPanel={showPaymentPanel}
              setShowPaymentPanel={setShowPaymentPanel}
              checkoutAmount={checkoutAmount}
              setCheckoutAmount={setCheckoutAmount}
              providerPhone={providerPhone}
              setProviderPhone={setProviderPhone}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              clientPhone={clientPhone}
              setClientPhone={setClientPhone}
              queryClient={queryClient}
              refetchClientOrders={refetchClientOrders}
              refetchProviderOrders={refetchProviderOrders}
              user={user}
              orderRefs={orderRefs}
              acceptMutation={acceptMutation}
              rejectMutation={rejectMutation}
              initiateCheckoutMutation={initiateCheckoutMutation}
              proceedToCheckoutMutation={proceedToCheckoutMutation}
              confirmCashMutation={confirmCashMutation}
              initiateStkPushMutation={initiateStkPushMutation}
            />
          </>
        )}
        {(providerLoading || clientLoading) && (
          <div className="text-center text-white py-12">Loading orders...</div>
        )}
        {user?.is_service_provider && providerOrders?.total_debt > 0 && (
          <div className="text-center text-yellow-500 mb-4">
            Total Debt: {providerOrders.total_debt} KES
          </div>
        )}
      </div>
    </div>
  );
}

export default ManageOrders;
